import React, { useState } from "react";
import { toast } from "sonner";
import { Shield, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { levelOf } from "@/lib/levels";

const FREEZE_PRICE = 15;
const FREEZE_MAX = 2;

export const LevelStreakCard = () => {
  const { user, refreshMe } = useAuth();
  const [buying, setBuying] = useState(false);
  const [phoneEdit, setPhoneEdit] = useState(false);
  const [phone, setPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [sharing, setSharing] = useState(false);
  if (!user) return null;
  const xp = user.xp || 0;
  const lv = levelOf(xp);
  const balance = user.coins_balance ?? (xp - (user.coins_spent || 0));
  const shields = user.streak_freezes || 0;
  const isAccountHolder = user.kind !== "token" && user.role !== "anonymous_token_student";

  const savePhone = async (value) => {
    setSavingPhone(true);
    try {
      await api.post("/auth/profile", { phone: value });
      await refreshMe();
      setPhoneEdit(false);
      toast.success(value ? "Got it — streak reminders will also come by text" : "Phone removed — email reminders only");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPhone(false); }
  };

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/gamification/streak-freeze/buy");
      toast.success(`🛡️ Streak shield bought — you hold ${data.streak_freezes}. It'll save your streak automatically if you miss a day.`);
      await refreshMe();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBuying(false); }
  };

  const shareStats = async () => {
    setSharing(true);
    try {
      const res = await api.get("/gamification/recap-image", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "self-agency-stats.png";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("📸 Stats graphic downloaded — post it anywhere!");
    } catch (e) { toast.error("Could not create your stats image"); }
    finally { setSharing(false); }
  };

  return (
    <div className="card mb-3 grid sm:grid-cols-2 gap-5" data-testid="level-streak-card">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs uppercase tracking-wider text-[#000000]">Level</span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-display font-extrabold text-white" style={{ background: lv.color }} data-testid="level-title-chip">
            {lv.title}
          </span>
        </div>
        {lv.next ? (
          <>
            <div className="h-2.5 rounded-full bg-[#EDEEF0] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${lv.pct}%`, background: lv.color }} data-testid="level-progress-fill" />
            </div>
            <div className="text-xs text-[#000000] mt-1.5" data-testid="level-progress-label">
              {xp.toLocaleString()} / {lv.next.min.toLocaleString()} lifetime coins to <b style={{ color: lv.next.color }}>{lv.next.title}</b>
            </div>
          </>
        ) : (
          <div className="text-sm font-semibold text-[#0A6B7A]" data-testid="level-max-label">★ Max level — you're a true Self-Agent.</div>
        )}
        <button className="btn-ghost text-xs underline underline-offset-2 text-[#0A6B7A] p-0 mt-2" data-testid="share-stats-btn"
          disabled={sharing} onClick={shareStats}>
          {sharing ? "Creating…" : "📸 Share my stats"}
        </button>
      </div>
      <div className="min-w-0 sm:border-l sm:border-[#EDEEF0] sm:pl-5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs uppercase tracking-wider text-[#000000]">Streak shields</span>
          {[...Array(FREEZE_MAX)].map((_, i) => (
            i < shields
              ? <ShieldCheck key={i} size={20} weight="fill" color="#0A6B7A" data-testid={`shield-held-${i}`} />
              : <Shield key={i} size={20} weight="duotone" color="#C4C8CE" data-testid={`shield-empty-${i}`} />
          ))}
        </div>
        <p className="text-xs text-[#000000] mb-2">A shield automatically saves your 🔥 streak if you miss a day — one shield per missed day.</p>
        <button className="btn-secondary text-sm px-3 py-1.5" onClick={buy}
          disabled={buying || shields >= FREEZE_MAX || balance < FREEZE_PRICE}
          data-testid="buy-streak-shield-btn">
          {shields >= FREEZE_MAX ? "Shields full (2/2)"
            : balance < FREEZE_PRICE ? `Need ${FREEZE_PRICE} coins (you have ${balance.toLocaleString()})`
            : buying ? "Buying…" : `Buy shield — ${FREEZE_PRICE} coins`}
        </button>
        {isAccountHolder && (
          <div className="mt-3 pt-2.5 border-t border-[#EDEEF0]">
            {!phoneEdit && !user.phone && (
              <button className="btn-ghost text-xs underline underline-offset-2 text-[#0A6B7A] p-0" data-testid="add-phone-btn"
                onClick={() => { setPhone(""); setPhoneEdit(true); }}>
                📱 Text me streak reminders too
              </button>
            )}
            {!phoneEdit && user.phone && (
              <div className="flex items-center gap-2 text-xs text-[#000000]" data-testid="phone-row">
                <span>📱 Texts on: •••-{user.phone.slice(-4)}</span>
                <button className="btn-ghost underline underline-offset-2 p-0 text-xs" data-testid="edit-phone-btn"
                  onClick={() => { setPhone(user.phone); setPhoneEdit(true); }}>change</button>
                <button className="btn-ghost underline underline-offset-2 p-0 text-xs text-[#991B1B]" data-testid="remove-phone-btn"
                  disabled={savingPhone} onClick={() => savePhone("")}>remove</button>
              </div>
            )}
            {phoneEdit && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <input className="input-base text-sm py-1.5 w-40" placeholder="403-555-0123" inputMode="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="phone-input" />
                <button className="btn-primary text-xs px-3 py-1.5" disabled={savingPhone} data-testid="save-phone-btn"
                  onClick={() => savePhone(phone)}>{savingPhone ? "Saving…" : "Save"}</button>
                <button className="btn-ghost text-xs" onClick={() => setPhoneEdit(false)}>Cancel</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
